import { inject, Injectable } from '@angular/core';
import { MatterportComponent } from '@simlab/matterport';
import { ComponentConfiguration, SpriteConfiguration } from '@simlab/simlab-facility-management/scene-object';
import { TransformationInterface } from '@simOn/common/matterport';
import { MarkerIcons, MarkerStateIconService, TComponentController } from '@simOn/common/scan';
import { TicketFacade } from '@simOn/ticket';
import { TicketTooltipComponent } from '@simOn/ticket/element/ui';
import { Ticket } from '@simOn/ticket/element/models';
import {
  catchError,
  concatAll,
  defer,
  filter,
  firstValueFrom,
  iif,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  tap
} from 'rxjs';
import { Vector3 } from 'three';
import { ComponentControllerBase } from './component-controller-base';
@Injectable()
export class TicketComponentControllerService extends ComponentControllerBase implements TComponentController {
  private readonly _ticketFacade = inject(TicketFacade);
  private readonly _markerStates = inject(MarkerStateIconService);
  readonly _nodes: Record<string, MatterportComponent> = {};
  private readonly _modes = ['tickets', 'tasks'] as const;

  override get mode() {
    return [...this._modes];
  }

  public set selectedMode(value: string) {
    this._modes.includes(value as (typeof this._modes)[number]) ? this._showAll() : this._hideAll();
  }

  readonly components$: Observable<void> = defer(() => this.selectedModel).pipe(
    switchMap(() =>
      this._ticketFacade.allTickets$.pipe(
        tap((tickets: Ticket[]) => {
          const ticketsWithPosition = tickets
            .filter((ticket: Ticket) => !ticket.positionDeleted && !!ticket.transformation?.position)
            .map((ticket: Ticket) => ticket.id);
          this._removeDeletedNotes(ticketsWithPosition);
        }),
        concatAll(),
        filter((ticket: Ticket) => {
          return !!ticket.transformation?.position && ticket.positionDeleted === false;
        }),
        mergeMap((ticket: Ticket) => {
          return iif(
            () => !!this._nodes[ticket.id!],
            of(true).pipe(
              tap(() => this._updateState(ticket)),
              take(1)
            ),
            of(true).pipe(
              switchMap(() => this.create3DComponent$(ticket)),
              take(1),
              catchError((e) => {
                console.error(e);
                return of(e);
              })
            )
          ).pipe(
            take(1),
            catchError((e) => {
              console.error(e);
              return of(e);
            })
          );
        })
      )
    )
  );

  removeDeletedAsset(id: string) {
    this.scanManagerService.component.deleteNote(id);

    if (this._nodes[id]) {
      delete this._nodes[id];
    }
  }

  restoreMarker(ticket: {
    id: string;
    transformation?: TransformationInterface | null;
    positionDeleted: boolean;
  }): void {
    this.removeDeletedAsset(ticket.id);
    if (!ticket.transformation?.position || ticket.positionDeleted) return;
    firstValueFrom(
      this.create3DComponent$({
        id: ticket.id,
        transformation: ticket.transformation,
        positionDeleted: false,
        name: '',
        htmlText: '',
        activities: [],
        attachments: [],
        status: 'Open',
        assignments: [],
        dueDate: '',
        hasUpdates: false,
        containsBlockedAttachments: false,
        createdAt: ''
      })
    );
  }

  create3DComponent$(ticket: Ticket) {
    const icons: MarkerIcons = this._markerStates.markerIcons(`/${$localize.locale}/assets/chat-note.png`);
    const component: ComponentConfiguration = {
      id: ticket.id,
      normal: ticket.transformation!.normalVector as Vector3,
      stemHeight: ticket.transformation!.stemLength || 0,
      position: ticket.transformation!.position as Vector3,
      scale: { x: 0.14, y: 0.14, z: 0.14 } as Vector3,
      up: new Vector3(0, 0, 1),
      userData: {
        type: 'tasks',
        noteId: ticket.id,
        taskId: ticket.id
      },
      tooltip: { component: TicketTooltipComponent },
      autoScale: false,
      objects: [
        new SpriteConfiguration({
          icon: icons.default,
          selectedHalo: icons.selected
        })
      ]
    };
    return this.runExclusiveMarkerOp$(ticket.id!, () => {
      this.scanManagerService.component.deleteNote(ticket.id);
      return this.scanManagerService.component.addComponentWithOffset$(component).pipe(
        tap((component: MatterportComponent) => {
          this._nodes[ticket.id!] = component;
        }),
        catchError((e) => {
          console.error(e);
          return of(e);
        })
      );
    });
  }

  private _removeDeletedNotes(tickets: string[]) {
    const currentAssets = Object.keys(this._nodes);
    if (currentAssets.length > 0) {
      const collection = [...currentAssets.filter((x) => !tickets.includes(x))];
      collection.forEach((id) => {
        if (this._nodes[id]) {
          this.scanManagerService.component.deleteNote(id);
          delete this._nodes[id];
        }
      });
    }
  }

  private _updateState(ticket: Ticket): void {
    this.scanManagerService.component.updatePositionWithOffset({
      id: ticket.id!,
      normal: ticket.transformation!.normalVector as Vector3,
      position: ticket.transformation!.position as Vector3,
      stemHeight: ticket.transformation!.stemLength || 0,
      scale: { x: 0.14, y: 0.14, z: 0.14 } as Vector3
    });
  }
}
